/**
 * The green screen keyed out, in one WebGL pass (KeyedVideo, the recorded-mouths player): how far each pixel's chroma
 * (YCbCr) is from the screen's sets its alpha, softly over `smoothness` and eroded by a pixel, and the green the
 * screen spilled is taken down to the mean of the other two channels. Any frame source: a video, a canvas, a bitmap.
 */
import type { ChromaKey } from '../stage/catalog';

const VERTEX = `
attribute vec2 a_pos;
varying vec2 v_uv;
void main() {
  v_uv = vec2(a_pos.x * 0.5 + 0.5, 0.5 - a_pos.y * 0.5);
  gl_Position = vec4(a_pos, 0.0, 1.0);
}`;

const FRAGMENT = `
precision mediump float;
varying vec2 v_uv;
uniform sampler2D u_frame;
uniform vec2 u_texel;
uniform vec2 u_key;
uniform float u_similarity;
uniform float u_smoothness;
uniform float u_spill;
vec2 chroma(vec3 c) {
  return vec2(-0.168736 * c.r - 0.331264 * c.g + 0.5 * c.b, 0.5 * c.r - 0.418688 * c.g - 0.081312 * c.b);
}
float keyed(vec2 uv) {
  // a zero smoothness is a hard edge (smoothstep is undefined when its two edges meet)
  return smoothstep(u_similarity, u_similarity + max(u_smoothness, 0.0001), distance(chroma(texture2D(u_frame, uv).rgb), u_key));
}
void main() {
  vec3 c = texture2D(u_frame, v_uv).rgb;
  // one pixel eaten off the edge: the video's chroma, compressed at half size, smears the green into it
  vec2 d = u_texel * 1.5;
  float alpha = min(keyed(v_uv), min(min(keyed(v_uv + vec2(d.x, 0.0)), keyed(v_uv - vec2(d.x, 0.0))),
                                     min(keyed(v_uv + vec2(0.0, d.y)), keyed(v_uv - vec2(0.0, d.y)))));
  c.g = mix(c.g, min(c.g, 0.5 * (c.r + c.b)), u_spill);
  gl_FragColor = vec4(c * alpha, alpha);
}`;

/** The chroma (Cb, Cr) of a `#rrggbb` colour, as the shader computes it. */
export function keyChroma(hex: string): [number, number] {
  const [r, g, b] = [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16) / 255) as [number, number, number];
  return [-0.168736 * r - 0.331264 * g + 0.5 * b, 0.5 * r - 0.418688 * g - 0.081312 * b];
}

function compile(gl: WebGLRenderingContext, type: number, source: string): WebGLShader {
  const shader = gl.createShader(type)!;
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(shader) ?? 'shader');
  return shader;
}

/** The program and its quad, once per canvas; the function it returns uploads a frame and keys it. Throws when the
 * shader does not compile. */
export function keyer(gl: WebGLRenderingContext, key: ChromaKey) {
  const program = gl.createProgram()!;
  gl.attachShader(program, compile(gl, gl.VERTEX_SHADER, VERTEX));
  gl.attachShader(program, compile(gl, gl.FRAGMENT_SHADER, FRAGMENT));
  gl.linkProgram(program);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) throw new Error(gl.getProgramInfoLog(program) ?? 'program');
  gl.useProgram(program);
  gl.bindBuffer(gl.ARRAY_BUFFER, gl.createBuffer());
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
  const pos = gl.getAttribLocation(program, 'a_pos');
  gl.enableVertexAttribArray(pos);
  gl.vertexAttribPointer(pos, 2, gl.FLOAT, false, 0, 0);
  gl.bindTexture(gl.TEXTURE_2D, gl.createTexture());
  for (const [name, value] of [[gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE], [gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE], [gl.TEXTURE_MIN_FILTER, gl.LINEAR], [gl.TEXTURE_MAG_FILTER, gl.LINEAR]] as const) {
    gl.texParameteri(gl.TEXTURE_2D, name, value);
  }
  gl.uniform2f(gl.getUniformLocation(program, 'u_key'), ...keyChroma(key.color));
  gl.uniform1f(gl.getUniformLocation(program, 'u_similarity'), key.similarity);
  gl.uniform1f(gl.getUniformLocation(program, 'u_smoothness'), key.smoothness);
  gl.uniform1f(gl.getUniformLocation(program, 'u_spill'), key.spill);
  const texel = gl.getUniformLocation(program, 'u_texel');
  return (frame: TexImageSource, width: number, height: number) => {
    const canvas = gl.canvas as HTMLCanvasElement;
    if (canvas.width !== width || canvas.height !== height) {
      canvas.width = width;
      canvas.height = height;
    }
    gl.viewport(0, 0, width, height);
    gl.uniform2f(texel, 1 / width, 1 / height);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, frame);
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT);
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
  };
}
